import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import CurrentUserData from "../queries/CurrentUserData";
import Spinner from "./Spinner";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Progress,
} from "reactstrap";

const Dashboard = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(CurrentUserData);

  if (loading) return <Spinner />;
  if (error) {
    console.log("error:");
    console.log(error);
    return <p>Something Went Wrong...</p>;
  }

  const { currentUserData } = data;
  const startDate =
    currentUserData.trial_type === "individual"
      ? "March 5, 2026"
      : "April 17, 2026";
  const dueDate =
    currentUserData.trial_type === "individual"
      ? "March 19, 2026"
      : "May 1, 2026";

  const totalSurveys = currentUserData.surveys.length;
  let completedSurveys = 0;

  for (const survey of currentUserData.surveys) {
    if (survey.completed_date !== undefined && survey.completed_date !== null) {
      completedSurveys++;
    }
  }

  return (
    <>
      <Row>
        <Col className="mb-4" xl="8">
          <Card className="bg-gradient-default">
            <CardBody>
              <Row>
                <Col lg="auto">
                  <img
                    alt="Clinet Logo"
                    className="clinet-logo-img mb-3 mb-lg-0"
                    src={require("../assets/img/brand/client-logo.png")}
                    style={{ maxHeight: "4rem", maxWidth: "100%" }}
                  />
                </Col>
                <Col>
                  <h3 className="card-title text-white mb-0">
                    Validation Trials: {startDate} - {dueDate}
                  </h3>
                  <hr className="my-2 bg-light" />
                  <h4 className="text-white mb-0 font-weight-normal">
                    Defense Language Institute Foreign Language Center (DLIFLC)
                  </h4>
                  <h4 className="text-white mb-0 font-weight-normal">
                    Advanced Language Enhancement Course (ALEC)
                  </h4>
                  <h4 className="text-white mb-0 font-weight-normal">
                    Course II: Arabic, Russian, and Korean
                  </h4>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col className="mb-4 mb-xl-0" xl="4">
          <Card className="bg-gradient-default">
            <CardBody>
              <Row>
                <Col>
                  <h5 className="text-uppercase text-muted mb-0 text-white card-title">
                    Completed Surveys
                  </h5>
                  <span className="h2 font-weight-bold mb-0 text-white">
                    {completedSurveys} / {totalSurveys}
                  </span>
                  <Progress
                    max="100"
                    value={(completedSurveys / totalSurveys) * 100}
                    color="success"
                    style={{ height: "4px" }}
                  />
                </Col>
              </Row>
              <Button
                onClick={() => {
                  navigate("/surveys");
                }}
                color="primary"
                size="sm"
                className="mt-3 mb-0"
              >
                View Surveys
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col className="mb-4 mb-xl-0" xl="8">
          <Card className="shadow-lg mb-4">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Purpose</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <p className="text-default font-weight-normal">
                The purpose of these trials is to ensure the learning objectives
                are met and the content functions properly on the Joint
                Knowledge Online (JKO) Learning Content Management System
                (LCMS), in operational environments. Your review is essential to
                ensure any functional issues are resolved and any other critical
                issues are addressed before these courses are made available to
                other members of the FAO community. Please note:
              </p>
              <ul className="text-default font-weight-normal pl-4">
                <li>
                  You are not being evaluated; the courseware is being
                  evaluated.
                </li>
                <li>Please do your best on the assessments.</li>
                <li>
                  To exit the course before completing it,{" "}
                  <strong>click on the Suspend Lesson button</strong> at the upper
                  right within the course window to save your progress; you can
                  resume later at the page where you exited.
                </li>
                <li>
                  Please complete each survey in its entirety; your feedback is
                  needed to determine the adequacy of the courseware for the
                  intended FAO audience.
                </li>
                <li>
                  <strong>Important</strong>: Please disconnect (
                  <strong>logout</strong>) from JKO when not taking the course.
                </li>
                <li>
                  Please <strong>keep track of the time</strong> it takes you to
                  complete each assigned module.
                </li>
              </ul>
            </CardBody>
          </Card>
          <Card className="shadow-lg">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">How to Proceed</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <p className="text-default font-weight-normal">
                Please complete a <strong>separate survey form</strong> for each
                module and assessment you review, and one for the course
                posttest (if assigned). Complete the survey by typing in the
                fields provided. Each survey is divided into three sections:
              </p>
              <ul className="text-default font-weight-normal pl-4">
                <li>Section 1: Administrative Information</li>
                <li>Section 2: Technical Problem/Error Log</li>
                <li>Section 3: General Feedback</li>
              </ul>
              <p className="text-default font-weight-normal">
                Familiarize yourself with all sections before starting your
                review. Since <strong>Section 2</strong> should be completed
                while you are reviewing the module, assessment, or posttest,
                please keep this survey open onscreen in a separate window as
                you review. During your review, please select/click on all
                selectable elements on each page to confirm each works as
                expected. For example, this would include using the controls for
                Audio/Visual (A/V) media to ensure these items play; selecting
                the e-Mentor icon where provided to view the e-Mentor Note
                information, and selecting answers for all module activity
                questions to ensure correct/incorrect feedback is shown.{" "}
              </p>
            </CardBody>
          </Card>
        </Col>
        <Col className="mb-4 mb-xl-0" xl="4">
          <Card className="shadow-lg mb-4">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Save and Submit</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <p className="text-default font-weight-normal">
                A SAVE button is provided at the end of each survey section.
              </p>
              <ul className="text-default font-weight-normal pl-4">
                <li style={{ marginBottom: "10px" }}>
                  Be sure to select <strong>SAVE</strong> after you complete
                  each section (or if you have partially completed your review
                  and survey, but need to log out and finish later).
                </li>
                <li>
                  After you have completed and saved Sections 1 and 3, you will
                  be prompted to submit your final survey. If you want to modify
                  any responses, do so before submitting your survey. Submitted
                  surveys can no longer be modified. When you are ready, save
                  any final changes and select <strong>SUBMIT</strong> when
                  prompted.
                </li>
              </ul>
            </CardBody>
          </Card>
          <Card className="shadow-lg">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Technical Support</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <p className="text-default font-weight-normal">
                If during your reviews, you experience technical difficulties
                that prevent you from progressing through the courseware, please
                use this email to request assistance:{" "}
              </p>
              <a
                href="mailto:FAO.language.courses@dliflc.edu"
                className="btn btn-primary btn-sm"
              >
                Email Support
              </a>
              {/* <p className="text-default font-weight-normal">Please complete your assigned module(s), assessment(s), course posttest (if assigned), and Validation Survey(s) by close of business on <strong>{dueDate}</strong>. Thank you very much for your time and assistance in this effort! </p> */}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Dashboard;
